import React, { useEffect, useState, Fragment } from 'react';
import { CheckCircle2, Compass, ShieldCheck, LayoutGrid, ArrowRight, Check, Lock, Star, X, Sparkles, BookOpen as BookOpenIcon, Download as DownloadIcon, RefreshCw as RefreshCwIcon, Briefcase as BriefcaseIcon, ChevronDown, ChevronUp, Award, Zap, FileText, Users } from 'lucide-react';
const MOCKUP_URL = 'https://cdn.magicpatterns.com/uploads/2jP1eBnpg3T2RfU6bfK9BX/Diseno_sin_titulo_(67).png';
const CHECKOUT_URL = 'https://adiccioness.impultienda.ar/';
const scrollToPurchase = (e: React.MouseEvent | React.KeyboardEvent) => {
  e.preventDefault();
  const el = document.getElementById('countdown-timer');
  if (el) el.scrollIntoView({
    behavior: 'smooth',
    block: 'start'
  });
};

// ─── Countdown ────────────────────────────────────────────────────────────────
const CountdownTimer = () => {
  const [timeLeft, setTimeLeft] = useState(0);
  useEffect(() => {
    const getEndOfDay = () => {
      const end = new Date();
      end.setHours(23, 59, 59, 999);
      return end.getTime();
    };
    const tick = () => setTimeLeft(Math.max(0, Math.floor((getEndOfDay() - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  const pad = (n: number) => n.toString().padStart(2, '0');
  const h = Math.floor(timeLeft / 3600);
  const m = Math.floor(timeLeft % 3600 / 60);
  const s = timeLeft % 60;
  return <div id="countdown-timer" className="bg-red-600 rounded-2xl shadow-lg p-4 mb-5 scroll-mt-2" data-id="element-0" data-mp="2zmwit1fkxsdu">
      <p className="text-white text-[10px] md:text-xs font-bold uppercase tracking-widest text-center mb-3" data-id="element-1" data-mp="33orkw85c49so">
        ⏰ Oferta válida solo hoy — termina en:
      </p>
      <div className="flex items-center justify-center gap-2" data-id="element-2" data-mp="3dlnsmbnesrw6">
        {[{
        label: 'Horas',
        v: h
      }, {
        label: 'Min',
        v: m
      }, {
        label: 'Seg',
        v: s
      }].map((unit, i) => <Fragment key={unit.label} data-id="element-3" data-mp="stubfl4ggf5b" data-mpi={i}>
              <div className="bg-[#edf2f0] rounded-xl px-3 md:px-5 py-2 min-w-[52px] md:min-w-[68px] shadow-inner text-center" data-id="element-4" data-mp="swqc3h7othxg">
                <div className="text-2xl md:text-3xl font-black text-red-600 leading-none tabular-nums" data-id="element-5" data-mp="3ud1gzdqblwcq">
                  {pad(unit.v)}
                </div>
                <div className="text-[9px] font-bold text-gray-400 uppercase tracking-wide mt-0.5" data-id="element-6" data-mp="d3wdn8ofjkul">
                  {unit.label}
                </div>
              </div>
              {i < 2 && <div className="text-white text-2xl font-black animate-pulse" data-id="element-7" data-mp="3glo067jsrcle">:</div>}
            </Fragment>)}
      </div>
    </div>;
};

// ─── Social Proof Popup ───────────────────────────────────────────────────────
const SocialProofPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [data, setData] = useState({
    name: '',
    city: ''
  });
  const names = ['María G.', 'Juan P.', 'Laura M.', 'Carlos R.', 'Sofía T.', 'Diego L.', 'Ana C.', 'Valentina S.', 'Martín F.', 'Camila R.'];
  const cities = ['Buenos Aires', 'Córdoba', 'Rosario', 'Mendoza', 'La Plata', 'Mar del Plata', 'Salta', 'Tucumán', 'Santa Fe', 'Neuquén'];
  useEffect(() => {
    const show = () => {
      setData({
        name: names[Math.floor(Math.random() * names.length)],
        city: cities[Math.floor(Math.random() * cities.length)]
      });
      setIsVisible(true);
      setTimeout(() => setIsVisible(false), 5000);
    };
    const t = setTimeout(show, 10000);
    const id = setInterval(show, 25000);
    return () => {
      clearTimeout(t);
      clearInterval(id);
    };
  }, []);
  if (!isVisible) return null;
  return <div className="fixed bottom-4 left-4 z-50 bg-[#edf2f0] rounded-xl shadow-2xl border border-[#dbe8e5] overflow-hidden max-w-xs" data-id="element-8" data-mp="1i3ulclmog3oa">
      <div className="bg-green-600 px-4 py-1.5 flex items-center gap-1.5" data-id="element-9" data-mp="1m44sw2q4b259">
        <ShieldCheck className="w-3.5 h-3.5 text-white" data-id="element-10" data-mp="6nw805kguvh9" />
        <span className="text-[10px] font-black text-white uppercase tracking-widest" data-id="element-11" data-mp="6qictxfz2wvs">Compra verificada</span>
      </div>
      <div className="p-3 flex items-start gap-3" data-id="element-12" data-mp="qx5hw2u8iwcl">
        <div className="bg-[#102c3c] rounded-full p-2 mt-0.5 shrink-0" data-id="element-13" data-mp="qxxcpbg0cq1t">
          <Check className="w-3.5 h-3.5 text-white" data-id="element-14" data-mp="m65jsu9dq88i" />
        </div>
        <div className="flex-1" data-id="element-15" data-mp="m4m5vab7499w">
          <p className="text-sm font-semibold text-gray-900 animate-pulse" data-id="element-16" data-mp="h6kmeo9uy7xw">{data.name} de {data.city}</p>
          <p className="text-xs text-gray-500 mt-0.5" data-id="element-17" data-mp="c9amufsr8fwc">Compró KIT Psicología de Adicciones™</p>
          <p className="text-[10px] text-gray-400 mt-0.5" data-id="element-18" data-mp="19w9pd764oi25">Hace unos minutos</p>
        </div>
        <button onClick={() => setIsVisible(false)} className="text-gray-300 hover:text-gray-500" data-id="element-19" data-mp="14wetep7hx2a8">
          <X className="w-4 h-4" data-id="element-20" data-mp="zxal00seb8ky" />
        </button>
      </div>
    </div>;
};

// ─── Accordion Card ───────────────────────────────────────────────────────────
type AccordionCardProps = {
  num?: string;
  tag?: string;
  title: string;
  description: string;
  benefits: string[];
  image: string;
  isBonus?: boolean;
};
const AccordionCard = ({
  num,
  tag,
  title,
  description,
  benefits,
  image,
  isBonus = false
}: AccordionCardProps) => {
  const [open, setOpen] = useState(false);
  return <div className={`rounded-2xl border transition-all duration-300 overflow-hidden ${open ? 'border-[#20b7ad] shadow-xl' : 'border-transparent shadow-md hover:shadow-lg'} bg-[#e8efed]`} data-id="element-21" data-mp="205qugxg084xd">
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} className="relative isolate w-full min-h-[172px] md:min-h-[210px] overflow-hidden text-left touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#20b7ad] focus-visible:ring-inset" data-id="element-22" data-mp="29vtn2wj7rgv6">
        <img src={image} alt="" className="absolute inset-0 -z-20 w-full h-full object-cover transition-transform duration-500 hover:scale-105" data-id="element-23" data-mp="2nwnxfkptnrwh" />
        <div className="absolute inset-0 -z-10 bg-[#102c3c]/70" data-id="element-24" data-mp="2sslani5sgi51" />
        <div className="absolute inset-0 p-5 md:p-7 flex flex-col justify-end" data-id="element-25" data-mp="tivb2qmhg872">
          <div className="flex items-start justify-between gap-4" data-id="element-26" data-mp="yfv9qgh068o5">
            <div className="space-y-2 max-w-[85%]" data-id="element-27" data-mp="21rkijvzz4ict">
              <div className="flex items-center gap-2" data-id="element-28" data-mp="21s3zb9dw10p9">
                {num && <span className="bg-[#20b7ad] text-white text-[10px] font-black px-2.5 py-1 rounded-full tracking-widest" data-id="element-29" data-mp="1wt0qdduseac0">ETAPA {num}</span>}
                {isBonus && <span className="bg-[#20b7ad] text-white text-[10px] font-black px-2.5 py-1 rounded-full tracking-widest" data-id="element-30" data-mp="2aq5uv9eur155">BONUS</span>}
              </div>
              <h3 className="text-xl md:text-3xl font-bold leading-tight text-white" data-id="element-31" data-mp="2kpmxtj2pz0xf">{title}</h3>
              <p className="text-xs md:text-sm text-[#d6e3e0] line-clamp-2" data-id="element-32" data-mp="2fs506ou5k84q">{description}</p>
            </div>
            <span className={`mt-auto shrink-0 w-9 h-9 rounded-full border border-white/40 flex items-center justify-center transition-colors ${open ? 'bg-[#20b7ad] border-[#20b7ad] text-white' : 'bg-white/10 text-white'}`} data-id="element-33" data-mp="2thoy9hcsgh7p">
              {open ? <ChevronUp className="w-5 h-5" data-id="element-34" data-mp="33h7i7xsp82zr" /> : <ChevronDown className="w-5 h-5" data-id="element-35" data-mp="2yi37v3u1fz7u" />}
            </span>
          </div>
        </div>
      </button>

      {open && <div className="bg-[#e8efed] border-t border-[#cddbd7] p-5 md:p-6 animate-[accordion-reveal_220ms_ease-out]" data-id="element-36" data-mp="3i4o9t0hc9vro">
          <p className="text-sm text-[#49616a] leading-relaxed max-w-2xl" data-id="element-37" data-mp="3n27og1hy8ak5">{description}</p>
          <ul className="mt-4 grid sm:grid-cols-3 gap-2.5" data-id="element-38" data-mp="384noxa3tn7ld">
            {benefits.map((b, i) => <li key={i} className="flex items-start gap-2 bg-[#f2f5f4] rounded-lg p-3" data-id="element-39" data-mp="3d2yvnbytl1zd" data-mpi={i}>
                <Check className="w-4 h-4 text-[#20b7ad] shrink-0 mt-0.5" strokeWidth={3} data-id="element-40" data-mp="2zcdb7itpwjzd" />
                <span className="text-xs text-[#294651] leading-snug font-medium" data-id="element-41" data-mp="344wy56eaorgu">{b}</span>
              </li>)}
          </ul>
        </div>}
    </div>;
};

// ─── Data ─────────────────────────────────────────────────────────────────────
const MODULES = [{
  num: '01',
  title: 'Evaluación inicial',
  description: 'Protocolo completo de primera consulta. Anamnesis, historia del consumo y evaluación del nivel de dependencia y motivación para el cambio.',
  benefits: ['Protocolo de primera consulta estructurado', 'Historia del consumo paso a paso', 'Evaluación de dependencia y motivación'],
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/7ea42dbb-6f4d-42bb-b047-3ce0f339b89d.jpg'
}, {
  num: '02',
  title: 'Diagnóstico y conceptualización del caso',
  description: 'Sistema para formular el caso clínico, identificar factores de riesgo, mantenimiento y protección, y establecer el encuadre terapéutico.',
  benefits: ['Formulación del caso clínico', 'Identificación de factores de riesgo y protección', 'Establecimiento del encuadre terapéutico'],
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/ba9fe884-b1a6-4729-8ff4-2b1f445f2688.jpg'
}, {
  num: '03',
  title: 'Plan de tratamiento',
  description: 'Diseño del plan de intervención por objetivos, etapas y técnicas. Adaptado a sustancias y juego patológico.',
  benefits: ['Plan de intervención por objetivos', 'Adaptado a sustancias y conductas adictivas', 'Etapas y técnicas concretas'],
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/2be6f255-02cb-4c22-9d75-53b911175d29.jpg'
}, {
  num: '04',
  title: 'Intervención en crisis y recaídas',
  description: 'Protocolo de actuación ante crisis de consumo, recaída y situaciones de alto riesgo. Qué hacer, qué decir y cómo contener sin perder el vínculo.',
  benefits: ['Protocolo ante crisis de consumo', 'Manejo de recaída sin perder el vínculo', 'Situaciones de alto riesgo: guía de acción'],
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/564ad8c6-65b8-4b71-9007-4699639160aa.jpg'
}, {
  num: '05',
  title: 'Trabajo motivacional',
  description: 'Sistema de intervención para sostener y fortalecer la motivación del paciente a lo largo del proceso de recuperación.',
  benefits: ['Técnicas de entrevista motivacional', 'Trabajo con ambivalencia', 'Fortalecimiento del compromiso terapéutico'],
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/fb2bc37d-710b-412c-83e2-8b23e00d60bd.jpg'
}, {
  num: '06',
  title: 'Entorno familiar y red de apoyo',
  description: 'Protocolo para trabajar con el entorno del paciente. Cómo involucrar a la familia sin que interfiera negativamente en el proceso.',
  benefits: ['Protocolo de trabajo con familia', 'Establecimiento de límites saludables', 'Fortalecimiento de la red de apoyo'],
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/f7ba6aee-d01c-46c2-9700-b1156cbe35b3.jpg'
}, {
  num: '07',
  title: 'Seguimiento y prevención de recaídas',
  description: 'Sistema de monitoreo del progreso, identificación de señales de alerta y estrategias concretas de prevención de recaída.',
  benefits: ['Monitoreo del progreso clínico', 'Identificación de señales de alerta', 'Estrategias concretas de prevención'],
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/ae535aa0-b904-437e-80c3-c5d894f393fa.jpg'
}, {
  num: '08',
  title: 'Alta y cierre del proceso',
  description: 'Protocolo de cierre terapéutico. Criterios de alta, sesión de cierre, plan de mantenimiento y seguimiento post alta.',
  benefits: ['Criterios de alta estructurados', 'Sesión de cierre terapéutico', 'Plan de mantenimiento y seguimiento'],
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/58dc041a-4a8e-439a-be2a-e042662ed492.jpg'
}];
const BONUSES = [{
  title: 'Abordaje de comorbilidades en adicciones',
  description: 'Guía clínica para identificar y trabajar trastornos concurrentes que complejizan el tratamiento de adicciones.',
  benefits: ['Identificación de comorbilidades frecuentes', 'Estrategias de intervención integrada', 'Criterios de derivación y coordinación'],
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/867c04f6-3173-40da-9a24-5b0bf51e1f5a.jpg'
}, {
  title: 'Protocolo clínico para intervención ante recaída',
  description: 'Protocolo paso a paso para actuar ante una recaída: cómo contener, evaluar y reencuadrar sin perder el vínculo terapéutico.',
  benefits: ['Protocolo de contención ante recaída', 'Reencuadre terapéutico post-recaída', 'Prevención de abandono del tratamiento'],
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/68e3a555-419e-4e3a-a9ca-0190ab4d1072.jpg'
}, {
  title: 'Guía de manejo del deseo intenso de consumo en consulta',
  description: 'Herramientas prácticas para trabajar el craving dentro de la sesión: técnicas de regulación, distracción y tolerancia al malestar.',
  benefits: ['Técnicas de regulación del craving', 'Ejercicios aplicables en sesión', 'Estrategias de tolerancia al malestar'],
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/ffa197a1-d4e7-4968-98eb-b1c8066cc756.jpg'
}, {
  title: 'Psicoeducación: Entendiendo la adicción',
  description: 'Documento descargable listo para entregar al paciente. Explica la adicción, el cerebro, la recaída y el proceso de recuperación en lenguaje accesible.',
  benefits: ['Material listo para entregar al paciente', 'Lenguaje claro y sin tecnicismos', 'Incluye mitos y verdades sobre la recuperación'],
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/62154d03-1f2e-4b80-a17e-a1e0f6c27d0a.jpg'
}, {
  title: 'Banco de preguntas para entrevista motivacional',
  description: 'Preguntas organizadas por etapas del proceso motivacional, listas para usar en sesión sin improvisar.',
  benefits: ['Preguntas por etapa del proceso', 'Trabajo con ambivalencia y compromiso', 'Abordaje de recaídas sin romper el vínculo'],
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/6469ad8e-3c89-429e-978a-67430c5eac30.jpg'
}];

// ─── App ──────────────────────────────────────────────────────────────────────
export function App() {
  return <div className="min-h-screen bg-[#f4f7f6] text-[#17313e] selection:bg-[#20b7ad] selection:text-white" data-id="element-42" data-mp="28471rworoidt">

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="bg-[#102c3c] text-white min-h-screen flex items-center px-4 py-10 md:px-6 md:py-16 relative overflow-hidden" data-id="element-43" data-mp="mlwnb8589sod">
        <div className="absolute top-0 left-0 h-1.5 w-full bg-[#20b7ad]" data-id="element-44" data-mp="qmgfryeuw846" />
        <div className="absolute top-12 right-[-7rem] h-64 w-64 rounded-full border border-white/10 pointer-events-none" data-id="element-45" data-mp="vn46eiz6amxv" />
        <div className="absolute bottom-[-9rem] left-[-5rem] h-80 w-80 rounded-full border border-[#20b7ad]/20 pointer-events-none" data-id="element-46" data-mp="10kmjmlk83obo" />

        <div className="max-w-6xl mx-auto w-full relative z-10 flex flex-col items-center text-center" data-id="element-47" data-mp="15io0czu6fbjz">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 px-3 py-1.5 rounded-full text-[10px] md:text-xs font-bold tracking-[0.14em] uppercase text-[#b7d7d1] shadow-sm" data-id="element-48" data-mp="1adlgsrtscn6w">
            <span className="h-1.5 w-1.5 rounded-full bg-[#20b7ad]" data-id="element-49" data-mp="1jaca4wgydnxs" />
            KIT PSICOLOGÍA DE ADICCIONES™
          </div>

          <h1 className="mt-5 max-w-5xl text-4xl leading-[0.98] sm:text-5xl md:mt-7 md:text-7xl lg:text-8xl font-bold tracking-[-0.055em] text-white" data-id="element-50" data-mp="1yfq7e07mbv21">
            Estructurá tus tratamientos de adicciones con criterio clínico.
          </h1>

          <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#c6d7d4] md:mt-6 md:text-xl" data-id="element-51" data-mp="1ohj24emoz1fv">
            Un sistema práctico para psicólogos que necesitan evaluar, planificar e intervenir con más orden, seguridad y menos tiempo de preparación.
          </p>

          <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-[11px] md:text-xs font-bold text-[#d8e7e4]" data-id="element-52" data-mp="2c6btdxsdcl45">
            <span className="bg-white/10 border border-white/15 rounded-full px-3 py-1.5" data-id="element-53" data-mp="27ebyijr5oief">8 etapas clínicas</span>
            <span className="bg-white/10 border border-white/15 rounded-full px-3 py-1.5" data-id="element-54" data-mp="23c76tqk9pvuf">5 recursos descargables</span>
            <span className="bg-white/10 border border-white/15 rounded-full px-3 py-1.5" data-id="element-55" data-mp="2v5hc0s9miylk">Uso profesional</span>
          </div>

          <div className="relative mt-2 md:mt-4 cursor-pointer group w-full max-w-3xl" onClick={scrollToPurchase} role="button" tabIndex={0} onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') scrollToPurchase(e);
        }} aria-label="Ver opciones del KIT" data-id="element-56" data-mp="t7nluxfx3oie">
            <div className="relative mx-auto flex min-h-[230px] items-center justify-center md:min-h-[390px]" data-id="element-57" data-mp="ofe2k09b5mp3">
              <div className="absolute left-[5%] top-[28%] hidden md:block bg-[#193b4b] rounded-2xl border border-white/10 p-4 text-left shadow-lg transition-transform duration-300 group-hover:-translate-y-1" data-id="element-58" data-mp="ajiktxqnahna">
                <BookOpenIcon className="h-5 w-5 text-[#20b7ad] mb-2" data-id="element-59" data-mp="5icto4cmv2fv" />
                <p className="text-xs font-bold text-white" data-id="element-60" data-mp="5il8i1sbghtp">8 etapas clínicas</p>
                <p className="text-[11px] text-[#b9d0cb] mt-1" data-id="element-61" data-mp="1uykkrrr8endk">De admisión a seguimiento</p>
              </div>
              <div className="absolute right-[5%] top-[22%] hidden md:block bg-[#193b4b] rounded-2xl border border-white/10 p-4 text-left shadow-lg transition-transform duration-300 group-hover:-translate-y-1" data-id="element-62" data-mp="1zvua77emez1e">
                <Sparkles className="h-5 w-5 text-[#20b7ad] mb-2" data-id="element-63" data-mp="3fd8nwi048uvk" />
                <p className="text-xs font-bold text-white" data-id="element-64" data-mp="3u59e6gch345u">5 bonus aplicables</p>
                <p className="text-[11px] text-[#b9d0cb] mt-1" data-id="element-65" data-mp="3pd7wgewjcl8f">Listos para sesión</p>
              </div>
              <div className="absolute bottom-[14%] left-[12%] hidden md:block bg-[#102c3c] rounded-2xl px-4 py-3 text-left shadow-lg transition-transform duration-300 group-hover:translate-y-1" data-id="element-66" data-mp="6yqmgz4okdkx">
                <p className="text-[11px] font-bold text-white" data-id="element-67" data-mp="gxy1dqr2cga5">Protocolos + plantillas</p>
              </div>
              <div className="absolute bottom-[14%] right-[12%] hidden md:block bg-[#193b4b] rounded-2xl border border-white/10 px-4 py-3 text-left shadow-lg transition-transform duration-300 group-hover:translate-y-1" data-id="element-68" data-mp="bzmqdcp17zqc">
                <p className="text-[11px] font-bold text-white" data-id="element-69" data-mp="2drj2ooj0xdm6">Uso profesional</p>
              </div>
              <img src={MOCKUP_URL} alt="KIT Psicología de Adicciones con Vínculos en Adicciones y 5 bonus clínicos" className="relative z-10 h-[228px] md:h-[369px] w-auto object-contain drop-shadow-2xl transition-transform duration-500 group-hover:scale-[1.025]" data-id="element-70" data-mp="1rwip6cf1un7k" />
            </div>
          </div>

          <button onClick={scrollToPurchase} className="mt-1 md:mt-3 bg-[#20b7ad] hover:bg-[#15958d] text-white font-bold text-sm md:text-base px-7 py-3.5 md:px-9 md:py-4 rounded-full shadow-[0_10px_24px_rgba(32,183,173,0.28)] transition-all duration-300 hover:-translate-y-0.5 flex items-center justify-center gap-2" data-id="element-71" data-mp="18ypyq9lm45r2">
            QUIERO VER TODO EL KIT
            <ArrowRight className="w-4 h-4 md:w-5 md:h-5" data-id="element-72" data-mp="2ubsr85j9495e" />
          </button>

          <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 w-full max-w-3xl text-left" data-id="element-73" data-mp="2zacbm1i75cvn">
            {[{
            label: 'Acceso inmediato',
            Icon: CheckCircle2
          }, {
            label: 'Descarga digital',
            Icon: DownloadIcon
          }, {
            label: 'Actualizaciones',
            Icon: RefreshCwIcon
          }, {
            label: 'Uso profesional',
            Icon: BriefcaseIcon
          }].map(({
            label,
            Icon
          }) => <div key={label} className="bg-white/10 border border-white/15 rounded-xl px-3 py-2.5 md:px-4 md:py-3 flex items-center gap-2 shadow-sm" data-id="element-74" data-mp="8yw7wns6qjo4">
                <Icon className="w-4 h-4 text-[#20b7ad] shrink-0" data-id="element-75" data-mp="3wx4jdwg3mr6" />
                <span className="text-[10px] md:text-xs font-bold text-[#d6e3e0] leading-tight" data-id="element-76" data-mp="3w21157ufpl6v">{label}</span>
              </div>)}
          </div>
        </div>
      </section>

      {/* ── POSITIONING ──────────────────────────────────────────────────── */}
      <section className="py-20 md:py-28 px-4 md:px-6 bg-[#e8efed]" data-id="element-77" data-mp="1pcw3xnnwtp63">
        <div className="max-w-5xl mx-auto text-center space-y-14" data-id="element-78" data-mp="1pa05zzc2b0hn">
          <div className="space-y-4 max-w-3xl mx-auto" data-id="element-79" data-mp="1kdsxbysa7aw0">
            <p className="text-[11px] md:text-xs font-black tracking-[0.2em] text-[#20b7ad] uppercase" data-id="element-80" data-mp="2c7vrh80s5sdx">
              Un sistema para trabajar adicciones con más orden clínico
            </p>
            <h2 className="text-3xl md:text-5xl font-bold text-[#102c3c] leading-tight" data-id="element-81" data-mp="22h9j9yve8459">
              KIT Psicología de Adicciones™
            </h2>
            <p className="text-lg md:text-xl text-[#4d626b] leading-relaxed" data-id="element-82" data-mp="1xhwtb5986gyg">
              Creamos un sistema claro y aplicable para que dejes de abordar casos de adicciones desde la improvisación, la intuición o la urgencia del momento… y empieces a conducir cada proceso con una estructura clínica concreta.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 text-left" data-id="element-83" data-mp="1eju0efdk3zvd">
            {[{
            Icon: Compass,
            title: 'Más claridad desde el inicio',
            body: 'Sabés qué evaluar, qué preguntar y cómo ordenar la información clínica del caso.'
          }, {
            Icon: ShieldCheck,
            title: 'Más seguridad profesional',
            body: 'Tenés protocolos para crisis, recaídas, motivación, familia y cierre del proceso.'
          }, {
            Icon: LayoutGrid,
            title: 'Más estructura en cada etapa',
            body: 'Guías, plantillas y recursos prácticos para aplicar en el trabajo clínico real.'
          }].map(({
            Icon,
            title,
            body
          }) => <div key={title} className="bg-[#f4f7f6] p-7 rounded-2xl border border-[#dbe8e5] hover:shadow-md transition-shadow" data-id="element-84" data-mp="3v6a4q8l5lvo6">
                <div className="bg-[#102c3c] w-11 h-11 rounded-xl flex items-center justify-center mb-5" data-id="element-85" data-mp="2yhc8sn1o3rpf">
                  <Icon className="w-5 h-5 text-[#20b7ad]" data-id="element-86" data-mp="32hmquy2qi8kn" />
                </div>
                <h4 className="text-base font-bold text-[#102c3c] mb-2" data-id="element-87" data-mp="37foabk42o9wn">{title}</h4>
                <p className="text-sm text-[#4d626b] leading-relaxed" data-id="element-88" data-mp="3cfsoh3hicsjp">{body}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* ── SISTEMA 8 ETAPAS ─────────────────────────────────────────────── */}
      <section className="py-20 md:py-28 px-4 md:px-6 bg-[#f4f7f6]" data-id="element-89" data-mp="rngxsemvs52z">
        <div className="max-w-4xl mx-auto space-y-10" data-id="element-90" data-mp="1zuxur6x7102">
          <div className="text-center space-y-4" data-id="element-91" data-mp="3t7h7r1k8fzpd">
            <p className="text-[11px] md:text-xs font-black tracking-[0.2em] text-[#20b7ad] uppercase" data-id="element-92" data-mp="3t6o07ydg9666">
              Qué incluye el KIT
            </p>
            <h2 className="text-3xl md:text-5xl font-bold text-[#102c3c]" data-id="element-93" data-mp="70inb8fi39s6">
              Sistema Psicología de Adicciones™
            </h2>
            <p className="text-base md:text-lg text-[#4d626b] max-w-2xl mx-auto" data-id="element-94" data-mp="3fgualdu4ehve">
              Un recorrido clínico completo, desde la evaluación hasta el seguimiento. Abrí solo los módulos que necesitás revisar.
            </p>
          </div>

          <div className="space-y-3" data-id="element-95" data-mp="3kf5j9l8oimc1">
            {MODULES.map(m => <AccordionCard key={m.num} {...m} data-id="element-96" data-mp="3kdufvmj5bglg" />)}
          </div>

          <div className="text-center pt-4" data-id="element-97" data-mp="2wo9s8qc1rv5u">
            <button onClick={scrollToPurchase} className="bg-[#20b7ad] hover:bg-[#15958d] text-white font-bold text-sm md:text-base px-7 py-3.5 md:px-9 md:py-4 rounded-full shadow-[0_10px_24px_rgba(32,183,173,0.28)] transition-all duration-300 hover:-translate-y-0.5 inline-flex items-center gap-2" data-id="element-98" data-mp="2rqhqz5piiwoz">
              QUIERO EL KIT COMPLETO
              <ArrowRight className="w-4 h-4 md:w-5 md:h-5" data-id="element-99" data-mp="x86ohr65eqj3" />
            </button>
          </div>
        </div>
      </section>

      {/* ── VÍNCULOS EN ADICCIONES ───────────────────────────────────────── */}
      <section className="vinculos-highlight-section px-4 py-10 md:px-6 md:py-14 bg-[#e8efed]" data-id="element-100" data-mp="1px46dt6g6lbb">
        <div className="vinculos-highlight-card max-w-4xl mx-auto overflow-hidden rounded-2xl border border-[#b8d3cc] bg-[#dce9e5] shadow-lg" data-id="element-101" data-mp="1uvnt3vsv814q">
          <div className="grid md:grid-cols-[0.9fr_1.1fr] items-stretch" data-id="element-102" data-mp="1kz2m54xacfy9">
            <img src="https://cdn.magicpatterns.com/patterns/generated-images/28b22020-8bc8-4ca4-8a29-dd74af627f0f.jpg" alt="Acompañamiento y vínculos familiares durante la recuperación" className="vinculos-highlight-image h-44 md:h-full min-h-[190px] w-full object-cover" data-id="element-103" data-mp="5jbvxzynefg2" />
            <div className="vinculos-highlight-content p-5 md:p-7 flex flex-col justify-center" data-id="element-104" data-mp="l023xxj3d9mf">
              <div className="inline-flex w-fit items-center gap-2 rounded-full bg-[#102c3c] px-3 py-1 text-[10px] font-black uppercase tracking-[0.14em] text-[#20b7ad]" data-id="element-105" data-mp="g31ynou2gawd">
                <Sparkles className="w-3.5 h-3.5" data-id="element-106" data-mp="18qflgu41vq2r" />
                Recurso incorporado
              </div>
              <h2 className="mt-3 text-2xl md:text-3xl font-bold text-[#102c3c]" data-id="element-107" data-mp="13r324jce114x">Vínculos en Adicciones</h2>
              <p className="mt-2 max-w-xl text-sm md:text-base leading-relaxed text-[#49616a]" data-id="element-108" data-mp="yrqg20tcby3e">
                Antes disponible por separado, ahora se suma al Kit sin costo extra: una guía práctica para trabajar familia, pareja y red de apoyo.
              </p>
              <div className="mt-4 flex items-center gap-3" data-id="element-109" data-mp="3jge6z8rxj2wy">
                <span className="text-sm font-bold text-[#718386] line-through" data-id="element-110" data-mp="3fgc9zcx52xo0">$12.000 ARS</span>
                <span className="text-2xl md:text-3xl font-black tracking-tight text-[#15958d]" data-id="element-111" data-mp="1v0edgasck65p">GRATIS</span>
                <span className="text-xs font-semibold text-[#49616a]" data-id="element-112" data-mp="1z181d45shkyl">incluido hoy</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── BONUSES ──────────────────────────────────────────────────────── */}
      <section className="py-20 md:py-28 px-4 md:px-6 bg-[#102c3c]" data-id="element-113" data-mp="2dq43ylp2u5rt">
        <div className="max-w-4xl mx-auto space-y-10" data-id="element-114" data-mp="2drxbe37iku0v">
          <div className="text-center space-y-4" data-id="element-115" data-mp="2inmbq8rcyey8">
            <div className="inline-block bg-[#20b7ad] text-white text-xs font-black px-4 py-1.5 rounded-full uppercase tracking-widest" data-id="element-116" data-mp="2mq9c4y23h871">
              ¡Gratis por el día de hoy!
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-white" data-id="element-117" data-mp="2rmznj82yffm4">
              5 Bonus Profesionales
            </h2>
            <p className="text-base md:text-lg text-[#8ab5b0] max-w-2xl mx-auto" data-id="element-118" data-mp="xat1tfcfpgfk">
              Recursos clínicos descargables incluidos con tu compra. Expandí cada uno para ver su contenido.
            </p>
          </div>

          <div className="space-y-3" data-id="element-119" data-mp="1vo6z8fpgp7jw">
            {BONUSES.map((b, i) => <AccordionCard key={i} {...b} isBonus data-id="element-120" data-mp="29ng80ubc8zdu" data-mpi={i} />)}
          </div>
        </div>
      </section>

      {/* ── PARA VOS SI ──────────────────────────────────────────────────── */}
      <section className="py-20 md:py-28 px-4 md:px-6 bg-[#e8efed]" data-id="element-121" data-mp="2jkku6u56ouun">
        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-12 items-center" data-id="element-122" data-mp="2jirlzaexa2kj">
          <div className="space-y-6" data-id="element-123" data-mp="2elrin79gjdx4">
            <p className="text-[11px] font-black tracking-[0.2em] text-[#20b7ad] uppercase" data-id="element-124" data-mp="2sd691z5p2he7">Este kit es para vos si…</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#102c3c] leading-tight" data-id="element-125" data-mp="2ocvuhy3u6inq">
              Trabajás con adicciones y querés más estructura clínica
            </h2>
            <ul className="space-y-4" data-id="element-126" data-mp="2xcrbijk2p7vo">
              {['Sos psicólogo/a o terapeuta y trabajás, o querés trabajar, con pacientes con adicciones.', 'Querés saber qué evaluar desde la primera consulta.', 'Te cuesta diseñar un plan de tratamiento claro para estos casos.', 'Querés tener protocolos para crisis, recaídas y situaciones de alto riesgo.', 'Buscás un sistema práctico, profesional y aplicable desde el primer día.'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-127" data-mp="2y68hgo6oofhq" data-mpi={i}>
                  <div className="bg-[#20b7ad] rounded-full p-0.5 mt-1 shrink-0" data-id="element-128" data-mp="32z1ib0z173oj">
                    <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} data-id="element-129" data-mp="2o9vnrqb521mz" />
                  </div>
                  <span className="text-sm md:text-base text-[#4d626b] leading-snug" data-id="element-130" data-mp="2t8gnlomhajaw">{item}</span>
                </li>)}
            </ul>
          </div>
          <div className="space-y-4" data-id="element-131" data-mp="2fb1vgc4ubxf8">
            <h3 className="text-xl md:text-2xl font-bold text-[#102c3c]" data-id="element-132" data-mp="2jb2a9xvuqamt">El resultado:</h3>
            <div className="space-y-3" data-id="element-133" data-mp="2zprvdv0ydojk">
              {['Entrás a la primera consulta con más claridad.', 'Diseñás un plan de tratamiento por objetivos y etapas.', 'Intervenís mejor ante crisis y recaídas.', 'Trabajás la motivación sin confrontación improductiva.', 'Cerrás el proceso con criterios de alta y seguimiento.'].map((item, i) => <div key={i} className="flex items-start gap-3 bg-[#edf2f0] p-3.5 rounded-xl border border-[#cddbd7]" data-id="element-134" data-mp="2grz2llk1muzw" data-mpi={i}>
                  <ArrowRight className="w-4 h-4 text-[#20b7ad] shrink-0 mt-0.5" data-id="element-135" data-mp="2bz5xgwknxkna" />
                  <span className="text-sm text-[#17313e] font-medium leading-snug" data-id="element-136" data-mp="271w1sz6okcr0">{item}</span>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* ── PURCHASE ─────────────────────────────────────────────────────── */}
      <section id="comprar" className="py-12 md:py-20 px-4 md:px-6 bg-[#f4f7f6]" data-id="element-137" data-mp="1t2o121uvcwkp">
        <div id="purchase-block" className="max-w-2xl mx-auto scroll-mt-2" data-id="element-138" data-mp="8k3sonw5l3ed">
          <div className="text-center space-y-2 mb-6" data-id="element-139" data-mp="cl5sp6xw9jhe">
            <p className="text-[11px] font-black tracking-[0.2em] text-[#20b7ad] uppercase" data-id="element-140" data-mp="cjkxdf3r9wih">Acceso completo</p>
            <h2 className="text-2xl md:text-4xl font-bold text-[#102c3c]" data-id="element-141" data-mp="hl1wb52oj8wf">
              El KIT completo para trabajar con más claridad clínica
            </h2>
          </div>

          <CountdownTimer data-id="element-142" data-mp="3l2aclkhre0p1" />

          <div className="bg-[#edf2f0] rounded-3xl shadow-2xl border border-[#cddbd7] overflow-hidden" data-id="element-143" data-mp="3kyw928xczlg0">
            {/* Header */}
            <div className="bg-[#102c3c] px-6 py-5 text-center" data-id="element-144" data-mp="3uzf1vt6wica2">
              <p className="text-[#20b7ad] text-xs font-black uppercase tracking-widest mb-1" data-id="element-145" data-mp="3ux29sn7filja">Oferta especial · Hoy</p>
              <h3 className="text-white text-xl md:text-2xl font-bold" data-id="element-146" data-mp="32ahj7oys2dgs">KIT Psicología de Adicciones™</h3>
            </div>

            <div className="p-5 md:p-8 space-y-5" data-id="element-147" data-mp="379155pddfp95">
              {/* Value items */}
              <div className="space-y-2" data-id="element-148" data-mp="2gzwx1f60g90w">
                {[{
                icon: FileText,
                label: 'Guía Principal Completa',
                value: '$10.000'
              }, {
                icon: LayoutGrid,
                label: 'Sistema en 8 etapas clínicas',
                value: '$18.000'
              }, {
                icon: Users,
                label: 'Vínculos en Adicciones',
                value: '$12.000'
              }, {
                icon: Award,
                label: '5 recursos clínicos descargables',
                value: '$10.000'
              }, {
                icon: Zap,
                label: 'Acceso inmediato + actualizaciones',
                value: '$5.000'
              }].map(({
                icon: Icon,
                label,
                value
              }) => <div key={label} className="flex items-center justify-between gap-3 bg-[#e8efed] px-4 py-3 rounded-xl border border-[#cddbd7]" data-id="element-149" data-mp="231p2fdwg5jsw">
                    <div className="flex items-center gap-2.5" data-id="element-150" data-mp="1tb49a5zjl7hd">
                      <Icon className="w-4 h-4 text-[#20b7ad] shrink-0" data-id="element-151" data-mp="1fajr9c1gbfox" />
                      <span className="text-xs md:text-sm font-semibold text-[#17313e]" data-id="element-152" data-mp="1fceb0n0zl1rf">{label}</span>
                    </div>
                    <span className="text-xs text-gray-400 line-through font-medium shrink-0" data-id="element-153" data-mp="10ncmjo2k7dyh">{value}</span>
                  </div>)}
              </div>

              <div className="bg-[#dce9e5] border border-[#b8d3cc] rounded-xl p-4 flex items-start gap-3" data-id="element-154" data-mp="vqmarcxsf4iv">
                <Users className="w-5 h-5 text-[#15958d] shrink-0 mt-0.5" data-id="element-155" data-mp="19ly3xfwaomxo" />
                <div data-id="element-156" data-mp="15le5m8zzjd9y">
                  <p className="text-sm font-bold text-[#17313e]" data-id="element-157" data-mp="15p1z585fjzoo">Ahora incluye: Vínculos en Adicciones</p>
                  <p className="text-xs text-[#49616a] leading-relaxed mt-1" data-id="element-158" data-mp="1endpd92dwvgl">Una guía práctica para intervenir con familia, pareja y red de apoyo, sin perder de vista el proceso individual del paciente.</p>
                </div>
              </div>

              {/* Total value */}
              <div className="bg-[#e8efed] rounded-xl px-4 py-3 flex items-center justify-between border border-[#cddbd7]" data-id="element-159" data-mp="1of9ydf60e5zl">
                <span className="text-xs font-black text-[#4d626b] uppercase tracking-wide" data-id="element-160" data-mp="1xe55ctgvnk3y">Valor total</span>
                <span className="text-sm font-black text-gray-400 line-through" data-id="element-161" data-mp="v0z5zz8yol38">$55.000 ARS</span>
              </div>

              {/* Savings badge */}
              <div className="bg-[#20b7ad]/10 border border-[#20b7ad]/30 rounded-xl px-4 py-3 flex items-center justify-between" data-id="element-162" data-mp="zzs8yxqmgtp5">
                <span className="text-sm font-bold text-[#15958d]" data-id="element-163" data-mp="18ru9sx4lsbzy">🎉 Tu ahorro hoy</span>
                <span className="text-sm font-black text-[#15958d]" data-id="element-164" data-mp="1dqxdoe8ukc6c">$33.000 ARS</span>
              </div>

              {/* Price */}
              <div className="bg-[#102c3c] rounded-2xl p-5 md:p-6 space-y-4 text-center" data-id="element-165" data-mp="1ip8mclneogmz">
                <div className="space-y-1" data-id="element-166" data-mp="1nolbxe5nw1t9">
                  <p className="text-[10px] font-black text-[#8ab5b0] uppercase tracking-widest" data-id="element-167" data-mp="1npwbf1s2qnek">Inversión especial de hoy</p>
                  <div className="text-lg text-gray-400 line-through font-bold" data-id="element-168" data-mp="3pghj6bksqpi8">$55.000 ARS</div>
                  <div className="flex items-end justify-center gap-2" data-id="element-169" data-mp="3piu7r7l27y49">
                    <span className="text-5xl md:text-6xl font-black text-white leading-none" data-id="element-170" data-mp="3ticw6dc3arbu">$22.000</span>
                    <span className="text-sm text-gray-400 font-semibold pb-1" data-id="element-171" data-mp="3m0n2a8hf0zni">ARS</span>
                  </div>
                </div>
                <p className="text-[#8ab5b0] text-xs font-medium" data-id="element-172" data-mp="3c8hb30yzmm9d">Pago único · Sin cargos ocultos · Sin suscripción</p>

                <a href={CHECKOUT_URL} className="block w-full bg-[#20b7ad] hover:bg-[#15958d] text-white font-black text-base md:text-xl px-6 py-4 md:py-5 rounded-xl shadow-[0_8px_30px_rgba(32,183,173,0.4)] transition-all duration-300 hover:-translate-y-0.5 uppercase tracking-wide" data-id="element-173" data-mp="387yjn810bi7p">
                  SÍ, QUIERO EL KIT AHORA
                </a>

                <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[10px] md:text-xs text-gray-400 font-medium pt-1" data-id="element-174" data-mp="3qygaf72a43qf">
                  <span className="flex items-center gap-1" data-id="element-175" data-mp="2kfhz8qtsre58"><Check className="w-3 h-3 text-[#20b7ad]" strokeWidth={3} data-id="element-176" data-mp="2fhyj1ldjgran" />Acceso inmediato</span>
                  <span className="flex items-center gap-1" data-id="element-177" data-mp="79jq3vrjsp36"><Check className="w-3 h-3 text-[#20b7ad]" strokeWidth={3} data-id="element-178" data-mp="78gzloy50ons" />Garantía 7 días</span>
                  <span className="flex items-center gap-1" data-id="element-179" data-mp="c1tdp556xd2a"><Check className="w-3 h-3 text-[#20b7ad]" strokeWidth={3} data-id="element-180" data-mp="3ueripqguaw3b" />+500 profesionales</span>
                </div>
              </div>

              {/* Trust badges */}
              <div className="grid grid-cols-3 gap-2 pt-1" data-id="element-181" data-mp="28cfzq1xndtc">
                {[{
                Icon: Lock,
                label: 'Pago seguro'
              }, {
                Icon: DownloadIcon,
                label: 'Descarga digital'
              }, {
                Icon: Users,
                label: '+500 psicólogos'
              }].map(({
                Icon,
                label
              }) => <div key={label} className="flex flex-col items-center gap-1.5 bg-[#e8efed] rounded-xl py-3 border border-[#cddbd7]" data-id="element-182" data-mp="lyfvtm64tq0m">
                    <Icon className="w-4 h-4 text-[#4c766d]" data-id="element-183" data-mp="31osqxnb9gkuy" />
                    <span className="text-[9px] md:text-[10px] font-bold text-[#4d626b] text-center leading-tight" data-id="element-184" data-mp="36onfhk7kcycu">{label}</span>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── COMPARATIVO DE MÉTODO ────────────────────────────────────────── */}
      <section className="py-16 md:py-20 px-4 md:px-6 bg-[#e8efed]" aria-labelledby="comparativo-title" data-id="element-185" data-mp="385192h7fqdiy">
        <div className="max-w-5xl mx-auto" data-id="element-186" data-mp="338atrw3gflyl">
          <div className="text-center max-w-2xl mx-auto mb-8 md:mb-10" data-id="element-187" data-mp="3356dw1gmn1s3">
            <p className="text-[11px] font-black tracking-[0.2em] text-[#20b7ad] uppercase mb-3" data-id="element-188" data-mp="2fholc3pjfzo2">Una forma distinta de trabajar</p>
            <h2 id="comparativo-title" className="text-2xl md:text-4xl font-bold text-[#102c3c] leading-tight" data-id="element-189" data-mp="2p785rdswg1l1">
              Del abordaje reactivo a una intervención con método
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-4 md:gap-6 items-stretch" data-id="element-190" data-mp="1wqnopmbw4g7o">
            <article className="rounded-2xl border border-[#cddbd7] bg-[#edf2f0] p-5 md:p-7" data-id="element-191" data-mp="1wokpqcsrfcqt">
              <div className="flex items-center gap-3 mb-5" data-id="element-192" data-mp="1rpzyh2vhk3ei">
                <div className="w-10 h-10 rounded-xl bg-[#d2ddda] flex items-center justify-center" data-id="element-193" data-mp="3rtan14ynbajx">
                  <FileText className="w-5 h-5 text-[#5f7377]" data-id="element-194" data-mp="mhjvvkal1cr" />
                </div>
                <div data-id="element-195" data-mp="5fk77lyh7eki">
                  <p className="text-[10px] font-black uppercase tracking-[0.16em] text-[#718386]" data-id="element-196" data-mp="5f0vwo3bzofg">Proceso actual</p>
                  <h3 className="text-xl font-bold text-[#294651]" data-id="element-197" data-mp="9i6ztp2z13ir">Sin el kit</h3>
                </div>
              </div>
              <ul className="space-y-3" data-id="element-198" data-mp="3duatbiwa05gw">
                {['Armás cada sesión desde cero', 'Dependés de la improvisación ante la resistencia', 'Te cuesta ordenar la evaluación inicial', 'No siempre sabés qué herramienta aplicar', 'El seguimiento post-alta queda disperso', 'Invertís tiempo extra preparando material', 'Te cuesta sostener una crisis de deseo en sesión'].map(item => <li key={item} className="flex items-start gap-2.5 text-sm text-[#52686d] leading-snug" data-id="element-199" data-mp="1c7ne1xq9flud">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#9aabaa] shrink-0" data-id="element-200" data-mp="17awxzaeggq8y" />
                    {item}
                  </li>)}
              </ul>
            </article>

            <article className="rounded-2xl bg-[#102c3c] p-5 md:p-7 shadow-xl border border-[#193b4b]" data-id="element-201" data-mp="ygiltgp81vu2">
              <div className="flex items-center gap-3 mb-5" data-id="element-202" data-mp="tjs5cs9i8y84">
                <div className="w-10 h-10 rounded-xl bg-[#20b7ad] flex items-center justify-center" data-id="element-203" data-mp="oikw0orprqzc">
                  <Zap className="w-5 h-5 text-white" data-id="element-204" data-mp="jjhrd5fnbmrw" />
                </div>
                <div data-id="element-205" data-mp="fifrcmdwn6ov">
                  <p className="text-[10px] font-black uppercase tracking-[0.16em] text-[#8ab5b0]" data-id="element-206" data-mp="fixy4cy713b1">Intervención con método</p>
                  <h3 className="text-xl font-bold text-white" data-id="element-207" data-mp="2dwr6pduvi2i2">Con el kit</h3>
                </div>
              </div>
              <ul className="space-y-3" data-id="element-208" data-mp="29x8hw6zxl79y">
                {['Evaluás y psicoeducás con material listo', 'Trabajás la motivación con preguntas por etapa', 'Contás con protocolos para recaída y crisis', 'Intervenís con la familia sin reforzar codependencia', 'Sostenés el seguimiento incluso después del alta', 'Comunicás avances con criterios clínicos objetivos', 'Dedicás más tiempo a intervenir que a preparar'].map(item => <li key={item} className="flex items-start gap-2.5 text-sm text-[#d6e3e0] leading-snug" data-id="element-209" data-mp="2eh3w2pq251ea">
                    <Check className="w-4 h-4 text-[#20b7ad] shrink-0 mt-0.5" strokeWidth={3} data-id="element-210" data-mp="20i471gymukhl" />
                    {item}
                  </li>)}
              </ul>
            </article>
          </div>

          <div className="mt-6 md:mt-8 bg-[#dce9e5] border border-[#b8d3cc] rounded-2xl px-5 py-5 text-center" data-id="element-211" data-mp="3ushlptamnxfm">
            <p className="text-xl md:text-2xl font-bold text-[#17313e] leading-snug" data-id="element-212" data-mp="3lwy6jjs77nc">
              No se trata de contener más. Se trata de tratar mejor.
            </p>
          </div>
        </div>
      </section>

      {/* ── FOOTER ───────────────────────────────────────────────────────── */}
      <footer className="bg-[#102c3c] text-[#8ab5b0] py-10 px-6 text-center text-sm" data-id="element-213" data-mp="mmcn32wa7e72">
        <div className="max-w-4xl mx-auto space-y-4" data-id="element-214" data-mp="hrov2u22k1nq">
          <div className="h-0.5 w-16 bg-[#20b7ad] mx-auto rounded-full mb-6" data-id="element-215" data-mp="whwfhfbmzur2" />
          <p data-id="element-216" data-mp="rjuvms6dzrej">© 2026 KIT Psicología de Adicciones. Todos los derechos reservados.</p>
          <p className="text-xs opacity-60 max-w-2xl mx-auto leading-relaxed" data-id="element-217" data-mp="15jdsupxntic2">
            Este sitio no es parte del sitio web de Facebook o Meta Inc. Además, este sitio NO está respaldado por Facebook de ninguna manera.
          </p>
        </div>
      </footer>

      <SocialProofPopup data-id="element-218" data-mp="37b8zgr526wnf" />

    </div>;
}